import React, { useEffect, useState } from "react";
import { Modal, Button, Form, Alert } from "react-bootstrap";
import createCustomSchema from "../../fieldValidator/fieldValidator";
import Swal from "sweetalert2";
import Loader from "../Loader";
import {
  assignTask,
  createTask,
  updateTask,
} from "../../services/taskServices";
import { fetchCompanyEmployees } from "../../services/EmployeeDetailServices";
import { employeeDetails } from "../../auth";

const CreateTaskModal = ({ show, handleClose, taskDetails, mode }) => {
  const employeeDirectory = employeeDetails();
  const [task, setTask] = useState({
    title: null,
    description: null,
    priority: null,
    deadline: null,
  });

  const [employees, setEmployees] = useState([]);
  const loginEmployeeId = localStorage.getItem("employeeID");

  const [error, setError] = useState({});
  const [loader, setLoader] = useState(false);
  const [extendDeadline, setExtendDeadline] = useState(false);

  const validationSchema = {
    title: createCustomSchema("paragraph", {
      message: "Please enter a title",
      min: 10,
    }),
    description: createCustomSchema("paragraph", {
      message: "Please enter a description",
      min: 10,
    }),
    priority: createCustomSchema("dropdown", {
      message: "Please select a priority",
      oneOf: ["low", "medium", "high"],
    }),
    deadline: createCustomSchema("date", {
      message: "Please select a valid deadline",
    }),
    ...(mode === "payment" && {
      payment_status: createCustomSchema("dropdown", {
        message: "Please select a payment status",
        oneOf: ["pending", "paid"],
      }),
    }),
    ...(mode === "assign" && {
      assigned_to: createCustomSchema("dropdown", {
        message: "Please select an employee to assign the task",
      }),
    }),
  };

  useEffect(() => {
    if (taskDetails) {
      setTask({
        title: taskDetails.title || "",
        description: taskDetails.description || "",
        priority: taskDetails.priority || "",
        deadline: taskDetails.deadline
          ? new Date(taskDetails.deadline).toISOString().split("T")[0]
          : "",
      });
    }
  }, [taskDetails]);

  useEffect(() => {
    getCompanyEmployees(employeeDirectory?.company?.company_id);
  }, [employeeDirectory?.company?.company_id]);

  const getCompanyEmployees = async (companyId) => {
    try {
      const response = await fetchCompanyEmployees(companyId);
      if (response.length > 0) {
        const employees = response.map((employee) => ({
          key: employee.id,
          value: employee.id,
          label: `${employee.first_name} ${employee.last_name}`,
        }));
        setEmployees(employees);
      } else {
        setEmployees([]);
      }
    } catch (error) {
      console.error("Error fetching employees:", error);
      setEmployees([]);
    }
  };

  const validateField = async (key, value) => {
    try {
      await validationSchema[key].validate(value);
      setError((prevErrors) => ({ ...prevErrors, [key]: null }));
    } catch (error) {
      setError((prevErrors) => ({ ...prevErrors, [key]: error.message }));
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setTask((prevTask) => ({
      ...prevTask,
      [name]: value,
    }));
    setError((prevErrors) => ({ ...prevErrors, createTask: null }));
    validateField(name, value);
  };

  const checkValidations = async () => {
    const newErrors = {};
    let hasError = false;

    await Promise.all(
      Object.keys(validationSchema).map(async (key) => {
        try {
          await validationSchema[key].validate(task[key]);
          newErrors[key] = null;
        } catch (error) {
          newErrors[key] = error.message;
          hasError = true;
        }
      })
    );

    setError(newErrors);
    return !hasError;
  };

  const onSubmit = async (e) => {
    setLoader(true);
    setError({
      ...error,
      createTask: null,
    });
    e.preventDefault();
    try {
      const hasError = await checkValidations();
      if (!hasError) return;
      const response = await createTask({
        ...task,
        created_by: loginEmployeeId,
      });
      if (response.success) {
        closeModal();
        Swal.fire({
          icon: "success",
          title: "Task",
          text: "Task created successfully!",
        });
      } else {
        setError({
          ...error,
          createTask:
            "Something went wrong while creating the task. Please try again.",
        });
      }
    } catch (error) {
      setError({
        ...error,
        createTask:
          "Something went wrong while creating the task. Please try again.",
      });
    } finally {
      setLoader(false);
    }
  };

  const resetTask = () => {
    setTask({
      title: null,
      description: null,
      priority: null,
      deadline: null,
    });
    setError({});
  };
  const closeModal = () => {
    resetTask();
    handleClose();
  };

  const assignEmployee = async () => {
    setLoader(true);
    setError({
      ...error,
      assignEmployee: null,
    });
    try {
      const hasError = await checkValidations();
      if (!hasError) return;
      // extendDeadline
      const response = await assignTask(
        taskDetails.task_id,
        task.assigned_to,
        loginEmployeeId
      );
      if (response.success) {
        closeModal();
        Swal.fire({
          icon: "success",
          title: "Task",
          text: "Employee assigned successfully!",
        });
      } else {
        setError({
          ...error,
          assignEmployee:
            "Something went wrong while assigning the employee. Please try again.",
        });
      }
    } catch (error) {
      setError({
        ...error,
        assignEmployee:
          "Something went wrong while assigning the employee. Please try again.",
      });
    } finally {
      setLoader(false);
    }
  };

  const priorities = [
    { key: "low", value: "Low" },
    { key: "medium", value: "Medium" },
    { key: "high", value: "High" },
  ];

  const priorityColors = {
    high: "danger",
    medium: "warning",
    low: "success",
  };

  const paymentStatus = [
    { key: "paid", value: "paid", label: "Paid" },
    { key: "pending", value: "pending", label: "Pending" },
  ];

  const onUpdate = async () => {
    setLoader(true);
    setError({
      ...error,
      updateTask: null,
    });
    try {
      const hasError = await checkValidations();
      if (!hasError) return;
      const response = await updateTask(taskDetails?.task_id, {
        ...task,
        updated_by: loginEmployeeId,
      });
      if (response.success) {
        closeModal();
        Swal.fire({
          icon: "success",
          title: "Task",
          text: `${
            mode === "edit"
              ? "Task updated successfully!"
              : "Payment processed successfully!"
          }`,
        });
      } else {
        setError({
          ...error,
          updateTask: "Something went wrong. Please try again.",
        });
      }
    } catch (error) {
      setError({
        ...error,
        updateTask: "Something went wrong. Please try again.",
      });
    } finally {
      setLoader(false);
    }
  };

  return (
    <Modal
      show={show}
      onHide={closeModal}
      backdrop="static"
      keyboard={false}
      size="lg"
    >
      <Modal.Header>
        <Modal.Title>
          {mode === "assign"
            ? "Assign"
            : mode === "edit"
            ? "Edit"
            : mode === "payment"
            ? "Process Payment of "
            : "Create"}{" "}
          Task
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form onSubmit={(e) => e.preventDefault()}>
          <Form.Group className="mb-2" controlId="formTitle">
            <Form.Label>
              Title{" "}
              <span
                className={`${
                  mode === "assign" || mode === "payment" ? "" : "text-danger"
                }`}
              >
                {mode === "assign" || mode === "payment" ? ":" : "*"}
              </span>
            </Form.Label>
            {mode === "assign" || mode === "payment" ? (
              <p className="ms-2 d-inline">{task.title}</p>
            ) : (
              <>
                <Form.Control
                  type="text"
                  placeholder="Enter task title"
                  name="title"
                  value={task.title}
                  onChange={handleChange}
                  required
                />
                {error.title && (
                  <span className="text-danger">* {error.title}</span>
                )}
              </>
            )}
          </Form.Group>
          <Form.Group className="mb-2" controlId="formDescription">
            <Form.Label>
              Description{" "}
              <span
                className={`${
                  mode === "assign" || mode === "payment" ? "" : "text-danger"
                }`}
              >
                {mode === "assign" || mode === "payment" ? ":" : "*"}
              </span>
            </Form.Label>
            {mode === "assign" || mode === "payment" ? (
              <p className="ms-2 d-inline">{task.description}</p>
            ) : (
              <>
                <Form.Control
                  as="textarea"
                  rows={3}
                  placeholder="Provide a detailed description of the task"
                  name="description"
                  value={task.description}
                  onChange={handleChange}
                />
                {error.description && (
                  <span className="text-danger">* {error.description}</span>
                )}
              </>
            )}
          </Form.Group>
          <div className="d-flex gap-2">
            <Form.Group
              className="mb-2 flex-grow-1 w-50"
              controlId="formPriority"
            >
              <Form.Label>
                Priority{" "}
                <span
                  className={`${
                    mode === "assign" || mode === "payment" ? "" : "text-danger"
                  }`}
                >
                  {mode === "assign" || mode === "payment" ? ":" : "*"}
                </span>
              </Form.Label>
              {mode === "assign" || mode === "payment" ? (
                <p
                  className={`ms-2 d-inline badge badge-${
                    priorityColors[task?.priority]
                  }`}
                >
                  {task.priority}
                </p>
              ) : (
                <>
                  <Form.Control
                    as="select"
                    name="priority"
                    value={task.priority}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Select priority</option>
                    {priorities.map((priority) => (
                      <option key={priority.key} value={priority.key}>
                        {priority.value}
                      </option>
                    ))}
                  </Form.Control>
                  {error.priority && (
                    <span className="text-danger">* {error.priority}</span>
                  )}
                </>
              )}
            </Form.Group>
            <Form.Group
              className="mb-2 flex-grow-1 w-50"
              controlId="formDeadline"
            >
              <Form.Label>
                Deadline{" "}
                <span
                  className={`${
                    mode === "assign" || mode === "payment" ? "" : "text-danger"
                  }`}
                >
                  {mode === "assign" || mode === "payment" ? ":" : "*"}
                </span>
              </Form.Label>
              {mode === "assign" || mode === "payment" ? (
                <>
                  <p className="ms-2 d-inline">
                    {task.deadline
                      ? new Date(task.deadline).toLocaleDateString()
                      : "No deadline set"}
                  </p>
                  {/* {mode === "assign" && (
                    <button className="ms-2 btn p-0 m-0 mb-1">
                      <i
                        title={
                          extendDeadline
                            ? "Click to close deadline extension"
                            : "Click to extend deadline"
                        }
                        className={`fa-solid fa-${
                          extendDeadline ? "circle-xmark" : "pen"
                        } ms-1 text-${extendDeadline ? "danger" : "success"}`}
                        onClick={() => setExtendDeadline(!extendDeadline)}
                      ></i>
                    </button>
                  )} */}
                </>
              ) : (
                <>
                  <Form.Control
                    type="date"
                    name="deadline"
                    value={task.deadline}
                    onChange={handleChange}
                    min={new Date().toISOString().split("T")[0]}
                  />
                  {error.deadline && (
                    <span className="text-danger">* {error.deadline}</span>
                  )}
                </>
              )}
            </Form.Group>
          </div>
          {mode === "assign" || mode === "payment" ? (
            <div className="mb-2 d-flex gap-2">
              <Form.Group className="mb-2 w-50" controlId="formAssignedTo">
                <Form.Label>
                  Assign To{" "}
                  <span
                    className={`${
                      mode === "assign" || mode === "payment"
                        ? ""
                        : "text-danger"
                    }`}
                  >
                    {mode === "assign" || mode === "payment" ? ":" : "*"}
                  </span>
                </Form.Label>
                {mode === "assign" ? (
                  <>
                    <Form.Control
                      as="select"
                      name="assigned_to"
                      value={task.assigned_to ?? taskDetails?.assigned_to ?? ""}
                      onChange={handleChange}
                    >
                      <option value="">Select an employee</option>
                      {employees.map((employee) => (
                        <option key={employee.key} value={employee.value}>
                          {employee.label}
                        </option>
                      ))}
                    </Form.Control>
                    {error.assigned_to && (
                      <span className="text-danger">* {error.assigned_to}</span>
                    )}
                  </>
                ) : (
                  <p
                    className={`ms-2 d-inline badge badge-info
                  }`}
                  >
                    {taskDetails?.assigned_to
                      ? employees.find(
                          (employee) => employee.key === taskDetails.assigned_to
                        )?.label || "Unassigned"
                      : "Unassigned"}
                  </p>
                )}
              </Form.Group>
              {extendDeadline ? (
                <Form.Group className="mb-2 w-50" controlId="formCompany">
                  <Form.Label>
                    Extend Deadline <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="date"
                    name="deadline"
                    value={task.deadline}
                    onChange={handleChange}
                  />
                  {error.deadline && (
                    <span className="text-danger">* {error.deadline}</span>
                  )}
                </Form.Group>
              ) : mode === "payment" ? (
                <Form.Group className="mb-2 w-50" controlId="formCompany">
                  <Form.Label>
                    Payment Status <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    as="select"
                    name="payment_status"
                    value={
                      task.payment_status ?? taskDetails?.payment_status ?? ""
                    }
                    onChange={handleChange}
                  >
                    <option value="">Select an status</option>
                    {paymentStatus.map((status) => (
                      <option key={status.key} value={status.value}>
                        {status.label}
                      </option>
                    ))}
                  </Form.Control>
                  {error.payment_status && (
                    <span className="text-danger">
                      * {error.payment_status}
                    </span>
                  )}
                </Form.Group>
              ) : (
                <></>
              )}
            </div>
          ) : (
            <></>
          )}
          {/* Error message for task creation */}
          <span className="text-danger">
            {error.createTask && `* ${error.createTask}`}
          </span>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" disabled={loader} onClick={closeModal}>
          Close
        </Button>
        {mode === "assign" ? (
          <Button variant="primary" disabled={loader} onClick={assignEmployee}>
            {loader ? "Assigning..." : "Assign"}
          </Button>
        ) : mode === "edit" || mode === "payment" ? (
          <Button variant="primary" disabled={loader} onClick={onUpdate}>
            {loader ? "Updating..." : "Update"}
          </Button>
        ) : (
          <Button variant="primary" disabled={loader} onClick={onSubmit}>
            {loader ? "Creating..." : "Create"}
          </Button>
        )}
      </Modal.Footer>
      <Loader show={loader} />
    </Modal>
  );
};

export default CreateTaskModal;
